import {
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnResizeMode,
  SortingState,
  OnChangeFn,
} from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BiSolidEditAlt, BiTask } from "react-icons/bi";
import { FaEye, FaUserCheck, FaUserShield } from 'react-icons/fa';
import { FaUserLarge } from 'react-icons/fa6';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuditObjectTypes, TableColumns, UserRequestGetParam, UserRequestTable } from '../../_core/_model.ts';
import { DeleteApproval, GetTableColumns, GetUserRequests } from '../../_core/_request.ts';
import { allAccessRoleID, IsAdminUser, rolesByDimensionItems } from '../../Constants.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import CreateButton from '../../UIComponents/CreateButton.tsx';
import DeleteRowButton from '../../UIComponents/DeleteRowButton.tsx';
import { AreObjectsEquals, getMyLocalTime } from '../../Utilities/Utilities.ts';
import ComponentLayout from '../Layouts/ComponentLayout.tsx';
import FileUploader from '../Tools/FileUploader.tsx';
import StatusCell from '../Tools/StatusCell.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import UserCell from '../Tools/UserCell.tsx';
import SearchAndAdvancedFilters, { appliedFilterType } from './SearchAndAdvancedFilters.tsx';
import TableLayout from './TableLayout.tsx';
import TableSummary from './TableSummary.tsx';
import UserWithDateCell from './UserWithDateCell.tsx';
import {
  startOfYear,
  endOfYear,
} from 'date-fns';
import moment from 'moment';
import TableLayoutEx, { colItem } from './TableLayoutEx.tsx';
import { useToast } from '../../context/ToastContext.tsx';

const iconButtonStyles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  approve: {
    backgroundColor: '#28a745',
    color: '#fff',
  },
  reject: {
    backgroundColor: '#dc3545',
    color: '#fff',
  },
};


const roleItems = Object.values(rolesByDimensionItems).flat();  // Flatten all roles across all dimensions

// Array.from({ length: 3 }, (_, i) => {
//   const statuses = ['Raised', 'Approved', 'Rejected'];
//   const randomRolesCount = Math.floor(Math.random() * (roleItems.length + 1)); // Random number of roles per user
//   const roles = [];

//   let totalWeight = 0;

//   // Randomly assign roles and calculate total weight
//   while (roles.length < randomRolesCount) {
//     const randomRole = roleItems[Math.floor(Math.random() * roleItems.length)];
//     if (!roles.includes(randomRole)) {
//       roles.push(randomRole);
//       totalWeight += randomRole.weight; // Add the weight of the assigned role
//     }
//   }

//   return {
//     name: `New User ${i + 1}`,
//     email: `newuser${i + 1}@example.com`,
//     raisedOn: new Date(Date.now() - Math.random() * 1e10).toISOString(),
//     status: statuses[Math.floor(Math.random() * statuses.length)],
//     roles: roles.map(role => role.name), // Store only the role names
//     weight: totalWeight // Total weight based on roles
//   };
// });

interface request {
  status: string | null,
  needButton: boolean,
  pushData: (rows: UserRequestTable[]) => void
}

export default function ApprovalTable({ status, needButton, pushData }: request) {
  const [myColumns, setMyColumns] = useState<TableColumns[]>([]);
  const { showToast } = useToast();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [rows, setRows] = useState<UserRequestTable[] | null>(null);
  const [totalLength, setTotalLength] = useState<number>(-1);
  const yearStartToEnd = () => {
    return moment().add(-12, "M").startOf("M").format("YYYY-MM-DD") + "," + moment().endOf("M").format("YYYY-MM-DD");
  }
  const defaultFilter = {
    stagingUserID: null,
    userName: null,
    status: null,
    userRequestedBy: null,
    userApprovedBy: null,
    createdAt: needButton ? yearStartToEnd() : null,
    approvedAt: null
  };

  const [statusFilter, setStausFilter] = useState<string | null>("");
  const [selectedFilter, setSelectedFilter] = useState<UserRequestGetParam>(defaultFilter);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dimensions, allUsers } = useAppContext();
  const uploadRef = useRef<any>(null);

  useEffect(() => {
    GetTableColumns(AuditObjectTypes.UserRequest).then((x) => {
      setMyColumns([...x.data]);
    })
  }, [])

  useEffect(() => {
    if (status != null || statusFilter != "")
      setStausFilter(status);
  }, [status])

  const getUserName = useCallback((row: any) => {
    if (typeof row.original != 'undefined') row = row.original;
    return (row.userFirstName == "" && row.userLastName == "") ? row.userName : row.userFirstName + " " + row.userLastName;
  }, []);

  const getMyUserName = useCallback((userId: number) => {
    let u = allUsers.find((x) => x.userID == userId);
    if (typeof u != 'undefined') return getUserName(u);
    return Math.random().toString();
  }, [allUsers, getUserName]);


  const getStatusArr = () => {
    if (status != null) {
      let stArr: string[] = [status];
      if (status == "approved")
        stArr = ["approved", "partially approved"];
      return stArr;
    }
    return [];
  }

  const filterredRecord = useMemo(() => {
    if (!rows) return [];
    return rows.filter((x) =>
      !statusFilter || getStatusArr().indexOf(x.status.toLowerCase()) > -1
    );
  }, [rows, statusFilter]);


  const dashboardColumns = useMemo(() => ({
    "Name": {
      accessorFn: (row) => getUserName(row), enableSorting: true,
      enableResizing: true, header: 'Name',
      cell: ({ cell, row }) => getUserName(row)
    },
    "status": {
      accessorKey: 'status', enableSorting: true,
      enableResizing: true, header: 'Status', size: 160, minSize: 120, cell: (cell) => {
        if (cell.getValue() == "" || cell.getValue() == "requested") {
          return <StatusCell status={"requested"}></StatusCell>
        }
        else
          return <StatusCell status={cell.getValue()}></StatusCell>
      }
    },
    "userRequestedBy": {
      accessorKey: 'userRequestedBy', enableSorting: true,
      enableResizing: true, header: "Requested by", size: 200, minSize: 100, cell: ({ cell, row }) => {
        return <UserWithDateCell {...{
          tDate: getMyLocalTime(row.original.createdAt),
          userId: cell.getValue()
        }} />
      }, sortFn: (a, b) => {
        const nameA = getMyUserName(a.original.userRequestedBy);
        const nameB = getMyUserName(b.original.userRequestedBy);
        return nameA.localeCompare(nameB);
      }
    },
    "userApprovedBy": {
      accessorKey: 'userApprovedBy', enableSorting: true,
      enableResizing: true, header: "Reviewed by", size: 200, minSize: 100, cell: (cell) => {
        return <UserCell userId={cell.getValue()} exUI={undefined} />
      }, sortFn: (a, b) => {
        const nameA = getMyUserName(a.original.userApprovedBy);
        const nameB = getMyUserName(b.original.userApprovedBy);
        return nameA.localeCompare(nameB);
      }
    },
    "action": {
      accessorKey: 'action', enableSorting: false,
      enableResizing: true, header: '', size: 180, cell: ({ cell, row }) => {
        return (
          (row.original.status == "" || row.original.status == "requested") ?
            <div className='d-flex flex-row align-items-center gap-2'>
              {
                IsAdminUser(user) &&
                <TooltipButton title="Approve/Reject">
                  <button className='t-requests-view'
                    style={{ ...iconButtonStyles.base, ...iconButtonStyles.approve }}
                    onClick={() => {
                      let id = row.original.stagingUserID;
                      navigate("/requests/edit?id=" + id);
                    }}
                  >
                    <BiSolidEditAlt size={16} />
                  </button>
                </TooltipButton>
              }
            </div> :
            <div><TooltipButton title="View"><button onClick={() => {
              let id = row.original.stagingUserID;
              navigate("/requests/edit?id=" + id);
            }} style={{ ...iconButtonStyles.base, ...iconButtonStyles.approve }}>
              <FaEye size={16} />
            </button>
            </TooltipButton>
            </div>
        )
      }
    }
  }), [getUserName, getMyUserName, user, navigate]);

  const columns = useMemo(() => ({
    "Name": {
      accessorFn: (row) => getUserName(row), header: 'Name', enableSorting: true,
      enableResizing: true, cell: ({ cell, row }) => getUserName(row)
    },
    "status": {
      accessorKey: 'status', header: 'Status', enableSorting: true,
      enableResizing: true, size: 120, minSize: 120, cell: (cell) => {
        if (cell.getValue() == "" || cell.getValue() == "requested") {
          return <StatusCell status={"requested"}></StatusCell>
        }
        else
          return <StatusCell status={cell.getValue()}></StatusCell>
      }
    },
    "userRequestedBy": {
      enableSorting: true,
      enableResizing: true, accessorKey: 'userRequestedBy', header: "Requested by", size: 150, minSize: 100,
      cell: ({ cell, row }) => {
        return <UserWithDateCell {...{
          tDate: getMyLocalTime(row.original.createdAt),
          userId: cell.getValue()
        }} />
      },
      sortFn: (a, b) => {
        const nameA = getMyUserName(a.original.userRequestedBy);
        const nameB = getMyUserName(b.original.userRequestedBy);
        return nameA.localeCompare(nameB);
      }
    },
    "userApprovedBy": {
      enableSorting: true,
      enableResizing: true, accessorKey: 'userApprovedBy', header: "Reviewed by", size: 150, minSize: 100,
      cell: ({ cell, row }) => {
        return <UserWithDateCell {...{
          tDate: getMyLocalTime(row.original.approvedAt),
          userId: cell.getValue()
        }} />
      },
      sortFn: (a, b) => {
        const nameA = getMyUserName(a.original.userApprovedBy);
        const nameB = getMyUserName(b.original.userApprovedBy);
        return nameA.localeCompare(nameB);
      }
    },
    "action": {
      accessorKey: 'action', size: 80, header: '', enableSorting: false,
      enableResizing: false, cell: ({ cell, row }) => (
        <div className='d-flex flex-row align-items-center gap-1'>
          <TooltipButton title="View Request">
            <button className='t-requests-view' onClick={() => {
              let id = row.original.stagingUserID;
              navigate("/requests/edit?id=" + id);
            }} style={{ ...iconButtonStyles.base, ...iconButtonStyles.approve }}>
              <FaEye size={16} />
            </button>
          </TooltipButton>
          {
            (IsAdminUser(user) || row.original.status.toLowerCase() == "requested") &&
            <DeleteRowButton {...{
              cell: cell, row: row, deleteAPI: (r) => {
                DeleteApproval(row.original.stagingUserID, user.userID).then((x) => {
                  if (x.data != "") {
                    showToast({
                      message: "Failed to Delete Request with error: " + x.data,
                      type: "warning",
                      duration: 5000
                    })
                  } else {
                    setTotalLength(-1);
                    setSelectedFilter(defaultFilter);
                    getRows(selectedFilter, undefined);
                  }
                });
              }, nameFieldId: "userName", title: "Request for User"
            }} />
          }
        </div>
      )
    }
  }), [getUserName, getMyUserName, user, navigate]);

  const getRows = (request: UserRequestGetParam, afterLoad: any) => {
    setRows(null);
    if (request.status != null) {
      let arr = request.status.split(",");
      let nStatus = "";
      for (var indx in arr) {
        if (nStatus != "")
          nStatus += ",";
        nStatus += "'" + arr[indx] + "'"
      }
      request.status = nStatus;
    }
    GetUserRequests(request).then((x) => {
      let fData = x.data;
      if (totalLength == -1)
        setTotalLength(fData.length);
      if (user.applicationAccess == 2)
        setRows([...fData]);
      else {
        fData = fData.filter((u) => u.userRequestedBy == user.userID || u.userApprovedBy == user.userID);
        setRows([...fData]);
      }
      pushData([...fData]);
      let sVal = searchParams.get("r");
      if (sVal != null) {
        let rId = parseInt(sVal);
        let u = fData?.find((x) => x.stagingUserID == rId);
        if (typeof u != 'undefined')
          setSelectedUser(u);
      }
      if (typeof afterLoad != 'undefined')
        afterLoad(x.data);
    })
  }

  const setSelectedUser = (u: UserRequestTable) => {
    navigate("edit?id=" + u.stagingUserID);
  }

  useEffect(() => {
    let sVal = searchParams.get("r");
    if (sVal != null) {
      let rId = parseInt(sVal);
      let u = filterredRecord.find((x) => x.stagingUserID == rId);
      if (typeof u != 'undefined')
        setSelectedUser(u);
    }
    else if (searchParams.get("s") != null)
      uploadRef.current?.startUpload();
  }, [searchParams])

  useEffect(() => {
    getRows(selectedFilter, undefined);
  }, [selectedFilter])


  const getDummyRoles = () => {
    let roles: any = {};
    for (var indx in dimensions) {
      let roleId = ((parseInt(indx) % 2) == 0) ? 0 : - 1;
      let tName = (roleId == allAccessRoleID[dimensions[indx].dimensionCode]) ? "Required all Access" : dimensions[indx].dimensionName + "=" + Math.random().toString();
      let cond = "RB_SBU_BU_SD´=´'GLF'´And´¼OM_BUS_UNIT´=´'RBAUS'´Or´¼RB_SBU_BU_SD´=´'GLF'´And´¼OM_BUS_UNIT´=´'RBAUS'´Or´¼";
      let dim = dimensions[indx].dimensionCode;
      roles[dim] = [{
        roleID: roleId,
        roleName: tName,
        roleCode: "",
        effectiveDate: "",
        status: "",
        roleType: "",
        description: "",
        dataMapCode: dim,
        notes: "",
        dimensionName: dim,
        weight: 0,
        createdBy: 0,
        dimensionID: 0,
        changed: false,
        conditions: cond,
      }];
    }
    return roles;
  }

  return (
    <ComponentLayout fixedLayout={!needButton} exButtons={undefined} instanceId={0} objectType={AuditObjectTypes.UserRequest} title={IsAdminUser(user) ? "User Security Request" : "User Security Requests Raised By Me"} bodyHeight={400}
      subHeader={
        <div className='d-flex flex-column w-100'>
          <SearchAndAdvancedFilters {...{
            filters: {
              "createdAt": {
                exValues: [],
                filterType: appliedFilterType.Date,
                icon: <FaUserLarge size={16} />,
                label: "Requested On"
              },
              "userRequestedBy": {
                exValues: [],
                filterType: appliedFilterType.User,
                icon: <FaUserLarge size={16} />,
                label: "Requested By"
              },
              "approvedAt": {
                exValues: [],
                filterType: appliedFilterType.Date,
                icon: <FaUserCheck size={16} />,
                label: "Reviewed On"
              },
              "userApprovedBy": {
                exValues: [],
                filterType: appliedFilterType.User,
                icon: <FaUserCheck size={16} />,
                label: "Reviewed By"
              },
              "status": {
                exValues: [{ key: "requested", value: "Requested" }, { key: "Partially Approved", value: "Partially Approved" }, { key: "Approved", value: "Approved" }, { key: "Rejected", value: "Rejected" }],
                filterType: appliedFilterType.Dropdown,
                icon: <BiTask size={16} />,
                label: "Status"
              },
            },
            values: {
              "status": null,
              "createdAt": null,
              "userRequestedBy": null,
              "approvedAt": null,
              "userApprovedBy": null,
              "userName": null
            },
            initialValues: defaultFilter,
            onChange: (filters, afterLoad) => {
              // fetchRecord({
              //   stagingUserID: "", 
              //   stagingUserID: filters["roleGroupID"].value,
              //   roleID: filters["roleId"].value, userID: filters["userID"].value, userName: filters["name"].value
              // }, afterLoad);
              //userID: number, userName: string, email: string, roleID: string | null, roleGroupID: string | null, managerUserID: string | null, appliationAccess: string | null
              getRows({
                stagingUserID: null,
                userName: filters.name,
                status: filters.status,
                userRequestedBy: filters.userRequestedBy,
                userApprovedBy: filters.userApprovedBy,
                createdAt: filters.createdAt,
                approvedAt: filters.approvedAt
              }, afterLoad);
            },
            exButton: <div className='d-flex flex-row align-items-center gap-2'>
              {/* {
                needButton ?
                  <FileUploader ref={uploadRef} onComplete={() => {
                    navigate("/requests/edit?id=-2")
                  }} /> :
                  <FileUploader ref={uploadRef} onComplete={() =>
                    setSelectedUser({
                      stagingUserID: -2,
                      managerUserID: 0,
                      managerExt: "",
                      userName: "Excel User",
                      userFirstName: "excel",
                      userLastName: "user",
                      email: "test@test.com",
                      role: "",
                      userExt: "11",
                      boNeeded: false,
                      requestDate: new Date().toLocaleDateString(),
                      replacingFormerUser: false,
                      formerUserID: 0,
                      status: "",
                      statusComment: "",
                      applicationAccess: 0,
                      bOLicenceUserID: 0,
                      internationalOnly: 0,
                      customerDimension: 0,
                      createdAt: new Date().toLocaleDateString(),
                      dimesionsRoles: getDummyRoles(),
                      sections: {},
                      userRequestedBy: 1,
                      userApprovedBy: 0,
                      roleGroups: "",
                      isAdUser: 0,
                      aComment: "",
                      rComment: "",
                      replaceRoles: false,
                      approvedAt: ""
                    })
                  } />
              } */}
              <div className='t-requests-create'>
                <CreateButton {...{
                  title: "Click to create a new security request for a user",
                  label: "Create",
                  onClick: () => {
                    navigate("/requests/create")
                  }
                }} />
              </div>
            </div>
          }} />
          <TableSummary {...{
            total: totalLength, filterred: filterredRecord.length, name: "Request",
            applyiedFilter: !AreObjectsEquals(selectedFilter, defaultFilter)
          }} />
        </div>
      } cBody={
        <TableLayoutEx needRowSelection={false} expandedUI={undefined} objType={AuditObjectTypes.UserRequest} rows={filterredRecord ?? []}
          columnDefinition={needButton ? dashboardColumns : columns} handleRowDoubleClick={(row) => {
            let id = row.original.stagingUserID;
            navigate("/requests/edit?id=" + id);
          }} emptyMessage={
            <AnimatePresence>
              <motion.div
                className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{
                  border: "2px dashed #dee2e6",
                  borderRadius: "10px",
                  marginTop: "0px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <FaUserShield size={60} color="#adb5bd" className="mb-3" />
                <h5 className="fw-semibold">No User Security Requests Found</h5>
                <p className="text-center" style={{ maxWidth: 400 }}>
                  It looks like there are no records yet. <br />Click{" "}
                  <button className='btn btn-link text-success p-0 m-0' onClick={() => { navigate("/requests/create") }}>Create new User Request</button> to get started.
                </p>
              </motion.div>
            </AnimatePresence>
          } needSelection={false} loading={rows == null} isSelected={(row) => {
            return false;
          }} setSelected={(row) => {
            if (!needButton) {
              // if (selectedUser?.stagingUserID != row.original.stagingUserID)
              // setSelectedUser(row.original)
              // else
              //   setSelectedUser(null)
            }
          }} />
      } />
  );
}
